import type { DiagnosticRow } from '@nowline/browser';
import { Editor } from '@nowline-app/editor';
import { useEffect, useState } from 'react';
import { downloadSource } from './toolbar/export.ts';

interface EditorPaneProps {
    value: string;
    onChange: (doc: string) => void;
    onDiagnostics?: (rows: DiagnosticRow[]) => void;
    /** Fired once when the CodeMirror editor first mounts — used for splash fade-out. */
    onReady?: () => void;
    /** Resolved colour scheme — forwarded to the CodeMirror theme compartment. */
    theme?: 'light' | 'dark';
}

export function EditorPane({ value, onChange, onDiagnostics, onReady, theme }: EditorPaneProps) {
    const [copied, setCopied] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: onReady is stable (useCallback)
    useEffect(() => {
        onReady?.();
    }, []);

    function handleCopy() {
        void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }

    function handleDownload() {
        downloadSource(value);
    }

    return (
        <div className='editor-pane'>
            <div className='editor-actions'>
                <button
                    type='button'
                    className='editor-action-btn'
                    title={copied ? 'Copied!' : 'Copy source'}
                    aria-label={copied ? 'Copied!' : 'Copy source'}
                    onClick={handleCopy}
                >
                    {copied ? (
                        <svg
                            viewBox='0 0 16 16'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='1.8'
                            aria-hidden='true'
                            width='14'
                            height='14'
                        >
                            <path
                                d='M3 8l3.5 3.5L13 5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </svg>
                    ) : (
                        <svg
                            viewBox='0 0 16 16'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='1.5'
                            aria-hidden='true'
                            width='14'
                            height='14'
                        >
                            <rect x='5' y='4' width='8' height='10' rx='1' />
                            <path d='M3 12V3a1 1 0 011-1h7' strokeLinecap='round' />
                        </svg>
                    )}
                </button>
                <button
                    type='button'
                    className='editor-action-btn'
                    title='Download .nowline file'
                    aria-label='Download .nowline file'
                    onClick={handleDownload}
                >
                    <svg
                        viewBox='0 0 16 16'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        aria-hidden='true'
                        width='14'
                        height='14'
                    >
                        <path d='M8 3v7' strokeLinecap='round' />
                        <path d='M5 7.5L8 11l3-3.5' strokeLinecap='round' strokeLinejoin='round' />
                        <path d='M3 13h10' strokeLinecap='round' />
                    </svg>
                </button>
            </div>
            <Editor
                value={value}
                onChange={onChange}
                className='editor-fill'
                {...(theme !== undefined ? { theme } : {})}
                {...(onDiagnostics ? { onDiagnostics } : {})}
            />
        </div>
    );
}
